import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Link,
	createFileRoute,
	useNavigate,
} from "@tanstack/react-router";
import { Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { useRequest } from "@/hooks/use-request";

import { requireAdmin } from "@/middleware/auth";
import { usePackListQuery } from "@/queries/pack";

export const Route = createFileRoute("/admin/")({
	beforeLoad: async () => {
		await requireAdmin();
	},
	component: RouteComponent,
});

const createPackSchema = z.object({
	pack_id: z
		.string()
		.min(1, "pack_id는 필수입니다.")
		.regex(/^[a-z0-9_-]+$/i, "영문/숫자/_/- 만 사용 가능합니다."),
	name: z.string().min(1, "이름은 필수입니다."),
	description: z.string().min(1, "설명은 필수입니다."),
	source: z.string().min(1, "출처는 필수입니다."),
});

type CreatePackValues = z.infer<typeof createPackSchema>;

function RouteComponent() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { data: packs, isLoading } = useQuery(usePackListQuery());
	const { post } = useRequest();

	const [open, setOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const form = useForm<CreatePackValues>({
		resolver: zodResolver(createPackSchema),
		defaultValues: {
			pack_id: "",
			name: "",
			description: "",
			source: "",
		},
	});

	const handleCreate = async (values: CreatePackValues) => {
		setSubmitting(true);
		try {
			await post<IResponse>("admin/pack", values);
			toast.success("Pack을 생성했습니다.");
			await queryClient.invalidateQueries({ queryKey: ["packs"] });
			setOpen(false);
			form.reset();
			navigate({
				to: "/admin/pack/$pack_id",
				params: { pack_id: values.pack_id },
			});
		} catch {
			// toast handled by useRequest
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="container mx-auto px-4 py-6 max-w-5xl">
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-2xl font-bold">관리자 페이지 — Pack 관리</h1>
				<Dialog open={open} onOpenChange={setOpen}>
					<DialogTrigger asChild>
						<Button>
							<Plus className="size-4" />새 Pack 만들기
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>새 Pack 만들기</DialogTitle>
						</DialogHeader>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(handleCreate)}
								className="space-y-4"
							>
								<FormField
									control={form.control}
									name="pack_id"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Pack ID</FormLabel>
											<FormControl>
												<Input placeholder="예: ans1" {...field} />
											</FormControl>
											<FormDescription>
												URL/스토리지 키에 쓰이는 짧은 식별자. 영문/숫자/_/- 만.
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="name"
									render={({ field }) => (
										<FormItem>
											<FormLabel>이름</FormLabel>
											<FormControl>
												<Input placeholder="예: 앱노트랙 환자설명자료" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="description"
									render={({ field }) => (
										<FormItem>
											<FormLabel>설명</FormLabel>
											<FormControl>
												<Textarea rows={3} {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="source"
									render={({ field }) => (
										<FormItem>
											<FormLabel>출처</FormLabel>
											<FormControl>
												<Input placeholder="예: 에이슬립" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<DialogFooter>
									<Button
										type="button"
										variant="ghost"
										onClick={() => setOpen(false)}
									>
										취소
									</Button>
									<Button type="submit" disabled={submitting}>
										{submitting ? (
											<Loader2 className="size-4 animate-spin" />
										) : (
											"생성"
										)}
									</Button>
								</DialogFooter>
							</form>
						</Form>
					</DialogContent>
				</Dialog>
			</div>

			{isLoading ? (
				<div className="flex justify-center py-20">
					<Loader2 className="size-8 animate-spin text-gray-400" />
				</div>
			) : !packs || packs.length === 0 ? (
				<div className="text-center text-gray-500 py-20">
					등록된 Pack이 없습니다.
				</div>
			) : (
				<ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{packs.map((pack) => (
						<li key={pack.id}>
							<Link
								to="/admin/pack/$pack_id"
								params={{ pack_id: pack.id }}
								className="block border rounded-lg overflow-hidden hover:border-gray-400 transition-colors"
							>
								<div className="aspect-video bg-gray-100 flex items-center justify-center">
									{pack.thumbnail_url ? (
										<img
											src={pack.thumbnail_url}
											alt={pack.name}
											className="w-full h-full object-cover"
										/>
									) : (
										<span className="text-gray-400 text-sm">
											썸네일 없음
										</span>
									)}
								</div>
								<div className="p-3">
									<div className="font-mono text-xs text-gray-500">
										{pack.id}
									</div>
									<div className="font-semibold truncate">{pack.name}</div>
									<div className="text-xs text-gray-500 mt-1">
										슬라이드 {pack.slide_count ?? 0}개 · 출처 {pack.source}
									</div>
								</div>
							</Link>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
